import React from 'react';
import { groupBy, sum, omit } from 'lodash';
import styled from 'styled-components';

import { useTranslation } from '@lt/localization';
import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';
import Summary from './Summary';
import { EXTRA_SCOPES, EXTRAS } from '../../store/constants/extras';
import FlightFuelIcon from '../../CheckoutRoutes/PackageCheckout/components/Flights/FlightFuelIcon';
import FlightSummaryItem from './FlightSummaryItem';
import { PriceDetails } from '../PriceDetails';
import OperatorDetails from './OperatorDetails';
import type { Item } from './types';

import {
  Experiment,
  EXPERIMENT_NAMES,
  Variant,
} from '../../../../components/Experiment';
import FlightCashback from './FlightCashback';
import type { ExtraItem } from '../../types/extras';
import type { TransformedBackFlight } from '../../CheckoutRoutes/PackageCheckout/types/flights';
import TransferMedInsuranceInfo from '../TransferMedInsuranceInfo';
import ManagerInfo from './components/ManagerInfo';

const baseCls = 'checkout-summary';

const pricesCls = `${baseCls}-prices`;

interface FlightSummaryProps {
  packagePrice: number;
  fuelCharge?: number;
  extras?: ExtraItem[];
  instantConfirm?: boolean;
  isMobile?: boolean;
  tourId: TransformedBackFlight['tour_id'];
  transfer: boolean;
  insurance: boolean;
  airports: {
    id: number;
    name: string;
    distance: number;
    transferTime: number;
  };
  transferPrivate: boolean;
}

const FlightSummary = ({
  packagePrice,
  fuelCharge = 0,
  extras = [],
  instantConfirm,
  tourId,
  transfer,
  insurance,
  transferPrivate,
  airports,
  isMobile,
}: FlightSummaryProps) => {
  const { t } = useTranslation();

  const fullPrice =
    packagePrice +
    fuelCharge +
    sum(extras && extras.map((extra) => extra.price));

  const extrasWithoutIcons = extras.map((item) => omit(item, 'icon'));
  const items: Item[] = [
    { name: t('checkout.flight.cost'), price: packagePrice },
    {
      name: t('checkout.flight.item.surcharge'),
      price: fuelCharge,
      scope: EXTRA_SCOPES.FLIGHT,
      icon: <FlightFuelIcon fuelCharge={fuelCharge} />,
    },
    ...extrasWithoutIcons,
  ];

  const { flightExtras, nonFlightExtras } = groupBy(
    items,
    ({ scope, code_name }) =>
      scope === EXTRA_SCOPES.FLIGHT && code_name !== EXTRAS.FLIGHT_LOCK
        ? 'flightExtras'
        : 'nonFlightExtras',
  );

  const hasWorseExtas = getIsFeatureHasAnOption('ab_worse_extras', 'on');

  return (
    <div className={`${baseCls}__wrapper`}>
      <Header>
        <Summary value={fullPrice} instantConfirm={instantConfirm} />
        <FlightCashback tourId={tourId} fullPrice={fullPrice} />
      </Header>
      <PriceDetails className={pricesCls}>
        {nonFlightExtras.map((item) => {
          const tooltipText =
            item.code_name === EXTRAS.FLIGHT_LOCK
              ? t('checkout.flight.flightLock')
              : undefined;

          return (
            <FlightSummaryItem
              key={item.name}
              tooltipText={tooltipText}
              {...item}
            />
          );
        })}
      </PriceDetails>
      <PriceDetails className={`${pricesCls} ${pricesCls}--flight-extras`}>
        {flightExtras.map((item) => {
          const name =
            item.code_name === EXTRAS.INFANT_BOARDING_PASS
              ? t('checkout.upgradeFlightClass.infant')
              : item.name;

          return (
            <FlightSummaryItem
              key={item.name}
              {...item}
              name={name}
              highlighted
            />
          );
        })}
      </PriceDetails>
      {hasWorseExtas ? (
        <TransferMedInsuranceInfo
          sidebar={false}
          transfer={transfer}
          insurance={insurance}
          airports={airports}
          transferPrivate={transferPrivate}
          isMobile={isMobile}
        />
      ) : null}
      <Experiment name={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.NAME}>
        <Variant option={EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES.OPTIONS.ON}>
          <OperatorDetails tourId={tourId} />
        </Variant>
      </Experiment>
      <ManagerInfo tourId={tourId} />
    </div>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 5px;
`;

export default FlightSummary;
